/* eslint-disable react/prop-types */
/* eslint-disable react/react-in-jsx-scope */
import styled from '@emotion/styled';
import _, { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import Theme from '../../../themes/index';
import TextInput from '../input-elements/oc-text-input';
import ContentGroupDropdown from '../oc-content-group-dropdown';

const SearchBar = styled.div`
  align-self: center;
  display: flex;
  margin-right: 0;

  input {
    -webkit-appearance: none;
    appearance: none;
    border-right: none;
    border: 1px solid #e4e4e4;
    display: block;
    font: inherit;
    height: 40px;
    margin-right: -2px;
    padding-left: 10px;
    width: 100%;
  }

  input::placeholder {
    color: #65656a;
    font-size: 13px;
    padding-top: 5px;
    text-transform: uppercase;
  }

  .search-button {
    background: transparent;
    border: 1px solid #e4e4e4;
    border-left: none;
    cursor: pointer;
    display: block;
    font-size: 16px;
    height: 40px;
    padding-bottom: 0;
    padding-left: 10px;
    padding-right: 10px;
    padding-top: 0;

    &:focus,
    &:hover {
      outline: 2px solid #65656a;
      outline-offset: -2px;
    }
  }

  input:focus + .search-button {
    border-color: ${props => props.themeColor};
    cursor: pointer;
    outline: none;
  }
`;

const ClearButton = ({ onClick }) => (
  <button
    className="search-button"
    type="submit"
    aria-label="Clear Search"
    onClick={onClick}
  >
    <span aria-hidden="true" className="fa fa-times fa-md" />
  </button>
);

const SearchButton = ({ onClick, disabled }) => (
  <button
    className="search-button"
    type="submit"
    aria-label="Submit Search"
    onClick={onClick}
    disabled={disabled}
    aria-disabled="true"
  >
    <span aria-hidden="true" className="fa fa-search fa-md" />
  </button>
);

const TextSearchFilter = ({
  onChange,
  value,
  filterAttribute,
  title,
  filterOnDebounce = true, // the onChange will fire shortly after the user stops typing
  filterOnEnter = false, // pressing enter will fire the onChange
  filterOnBlur = false, // the onChange will fire when the input loses focus
}) => {
  const [text, setText] = useState(value);
  // keep the debounce function between renders, or else we can't cancel/refresh it
  const debounceUpdate = useMemo(
    () =>
      debounce((filterAttr, newValue) => {
        // Preserve the existing parameters and only update `q`
        onChange(filterAttr, newValue);
      }, 300),
    [onChange]
  );

  // upon rendering with a new text value, kick off a debounce to update the filter
  useEffect(() => {
    if (filterOnDebounce) {
      if (value !== text) {
        debounceUpdate(filterAttribute, text);
      } else {
        debounceUpdate.cancel();
      }
    }
  }, [text, filterAttribute, value]);

  // Reset text when the value prop changes to undefined or an empty string
  useEffect(() => {
    if (_.isEmpty(value)) {
      setText('');
    }
  }, [value]);

  const onTextChange = e => {
    setText(e.target.value);
  };

  const clearSearch = () => {
    onChange(filterAttribute, '');
    setText('');
  };

  // push the text input value to the filter
  const updateFilter = () => {
    if (value !== text) {
      onChange(filterAttribute, text);
    }
  };

  const Wrapper = filterOnEnter ? 'form' : 'div';
  const showSearchButton = filterOnDebounce ? !text : !text || value !== text;

  return (
    <Wrapper>
      <ContentGroupDropdown
        label={title}
        labelFor="item_search"
        collapsible={false}
      >
        <SearchBar themeColor={Theme.colors.brand}>
          <TextInput
            name="item_search"
            onBlur={filterOnBlur ? updateFilter : null}
            onChange={onTextChange}
            value={text}
          />
          {showSearchButton ? (
            <SearchButton onClick={updateFilter} disabled={value === text} />
          ) : (
            <ClearButton onClick={clearSearch} />
          )}
        </SearchBar>
      </ContentGroupDropdown>
    </Wrapper>
  );
};

TextSearchFilter.filterValueType = 'singular';
TextSearchFilter.getChipInfo = (_filter, filterValue) => {
  if (!filterValue || filterValue.length === 0) {
    return [];
  }

  return [
    {
      value: filterValue,
      displayName: `"${filterValue}"`,
    },
  ];
};

export default TextSearchFilter;
